export type SyberryTodayTaskId = string;
type SyberryTodayProjectId = string;

export enum SyberryTodayWorkItemState {
  QUEUE = 'queue',
  DO_TODAY = 'doToday',
}

export enum SyberryTodayTaskStatus {
  NEW = 'new',
  IN_PROGRESS = 'inProgress',
  RESOLVED = 'resolved',
}

export enum SyberryTodayPersonalBacklogCategory {
  INBOX = 'inbox',
  BLOCKED = 'blocked',
  TODAY = 'today',
  ALL = 'all',
  ONGOING_ACTIVITY = 'ongoing_activity',
  PARENT_TASKS = 'parent_tasks',
}

export enum SyberryTodayPlanningStep {
  SELECT_TASKS = 'SELECT_TASKS',
  ORGANIZE_TASKS = 'ORGANIZE_TASKS',
  CONFIRMED = 'CONFIRMED',
}

export enum SyberryTodayPlanStatus {
  NOT_STARTED = 'NOT_STARTED',
  SELECTING = 'SELECTING',
  ORGANIZING = 'ORGANIZING',
  CONFIRMED = 'CONFIRMED',
  SKIPPED = 'SKIPPED',
  POSTPONED = 'POSTPONED',
}

export enum SyberryTodayAllocationStatus {
  ON_TRACK = 'ON_TRACK',
  BEHIND = 'BEHIND',
  AHEAD = 'AHEAD',
  COMPLETED = 'COMPLETED',
}

export enum SyberryTodaySpentTimeActivity {
  OFFICE = 'OFFICE',
  HOME = 'HOME',
  TRANSIT = 'TRANSIT',
}

export interface SyberryTodayProject {
  id: SyberryTodayProjectId;
  name: string;
  key: string;
}

export interface SyberryTodayProjectAllocationSummary {
  projectId: SyberryTodayProjectId;
  allocationLabel: string;
  plannedMinHours: number;
  plannedMaxHours: number;
  spentHours: number;
  remainingMinHours: number;
  remainingMaxHours: number;
  exceededMaxHours: number;
  progressPercent: number;
  minAllocationMarkerPercent: number;
  recommendedHoursToday: number;
  status: SyberryTodayAllocationStatus;
  guidanceMessage: string;
}

export interface SyberryTodayTaskGroup {
  id: string;
  projectId: SyberryTodayProjectId;
  name: string;
  totalCount: number;
  unreadCount: number;
}

export interface SyberryTodayTaskStream {
  id: string;
  name: string;
  projectId: SyberryTodayProjectId;
  groupId: string;
}

export interface SyberryTodayWorkItem {
  id: SyberryTodayTaskId;
  issueKey: string;
  title: string;
  projectId: SyberryTodayProjectId;
  projectName: string;
  groupId: string;
  streamId: string;
  streamName: string;
  redmineUrl: string;
  state: SyberryTodayWorkItemState;
  selectedForToday: boolean;
  todayOrder?: number;
  personalBacklogPosition?: number;
  customQueuePosition?: number;
  personalBacklogCategory: SyberryTodayPersonalBacklogCategory;
  isRecurrent: boolean;
  isUnread: boolean;
  assignee?: string;
  addedBy?: string;
  addedAt?: string;
  dueDate?: Nullable<string>;
  status: SyberryTodayTaskStatus;
  description?: string;
  notes?: string;
  updatedAt: string;
}

interface SyberryTodayAvailableTasks {
  projects: SyberryTodayProject[];
  groups: SyberryTodayTaskGroup[];
  streams: SyberryTodayTaskStream[];
  tasks: SyberryTodayWorkItem[];
}

export interface SyberryTodayPlanning extends SyberryTodayAvailableTasks {
  status: SyberryTodayPlanStatus;
  selectedTaskIds: SyberryTodayTaskId[];
  systemWarning: Nullable<string>;
}

export interface SyberryTodayConfirmCheckInPayload {
  taskIds: SyberryTodayTaskId[];
}

export interface SyberryTodaySpentTimePayload {
  taskId: SyberryTodayTaskId;
  issueKey: string;
  date: string;
  timeSpent: string;
  comment: string;
  activity: SyberryTodaySpentTimeActivity;
}

export interface SyberryTodayService {
  getTodayPlanning: () => Promise<SyberryTodayPlanning>;
  getTodayAllocations: () => Promise<SyberryTodayProjectAllocationSummary[]>;
  saveTaskSelection: (
    taskIds: SyberryTodayTaskId[],
    planningSnapshot?: SyberryTodayPlanning,
  ) => Promise<SyberryTodayPlanning>;
  markTaskAsOpened: (taskId: SyberryTodayTaskId) => Promise<void>;
  confirmCheckIn: (payload: SyberryTodayConfirmCheckInPayload) => Promise<SyberryTodayPlanning>;
  postponePlanning: () => Promise<SyberryTodayPlanning>;
  skipCheckInCompletely: () => Promise<SyberryTodayPlanning>;
  reorderQueueIssues: (queueId: number, orderedTaskIds: SyberryTodayTaskId[]) => Promise<SyberryTodayPlanning>;
  createSpentTime: (payload: SyberryTodaySpentTimePayload) => Promise<SyberryTodaySpentTimePayload>;
}
